import type { ticketDetails } from 'shared';
import type { flightEntity } from 'freedom-shared';

import {
  directFlightsLib,
  type directFlightsTypes,
} from '@/widgets/direct-flights';

import type { TimeItem, TimeMapValue } from '../types';

/**
 * Updates timeMap with the lowest price for a given key.
 * Only considers price; does not prefer enabled over disabled.
 */
function updateTimeMapWithLowestPrice(
  timeMap: Map<string, TimeMapValue>,
  key: string,
  price: number,
  date: string,
  isDisabled?: boolean,
): void {
  const existing = timeMap.get(key);
  const newEntry: TimeMapValue = { price, date, isDisabled };
  if (!existing) {
    timeMap.set(key, newEntry);
    return;
  }
  if (price < existing.price) {
    timeMap.set(key, newEntry);
  }
}

/**
 * Converts timeMap entries to TimeItem array with selection state and price diff
 */
function buildTimeItemsFromMap(
  timeMap: Map<string, TimeMapValue>,
  currentTime: string,
  currentDate: string,
  selectedPrice: number,
  isRoundTrip: boolean,
): TimeItem[] {
  return Array.from(timeMap.entries())
    .map(([timeKey, data]) => {
      const [time] = timeKey.split('-');
      const isSelected =
        time === currentTime && (!isRoundTrip || data.date === currentDate);
      const priceDiff = isSelected ? null : data.price - selectedPrice;

      return {
        time,
        date: data.date,
        price: data.price,
        priceDiff,
        isSelected,
        isDisabled: data.isDisabled,
      };
    })
    .sort((a, b) => a.time.localeCompare(b.time));
}

function getTimesFromOffersFallback(
  offers: flightEntity.types.MappedOffer[],
  flightIndex: number,
  filterByOtherDirection?: { time: string; date: string },
): { times: string[]; availableTimes: string[] } {
  const times = Array.from(
    new Set(
      offers
        .map(offer => offer.flights[flightIndex]?.segments[0]?.dep?.time)
        .filter((t): t is string => !!t),
    ),
  ).sort();

  const availableTimes = filterByOtherDirection
    ? Array.from(
        new Set(
          offers
            .filter(o => {
              const { time, date } =
                directFlightsLib.getCurrentFlightTimeAndDate(
                  o,
                  flightIndex === 0 ? 1 : 0,
                );
              return (
                time === filterByOtherDirection.time &&
                date === filterByOtherDirection.date
              );
            })
            .map(
              o =>
                directFlightsLib.getCurrentFlightTimeAndDate(o, flightIndex)
                  .time,
            )
            .filter((t): t is string => !!t),
        ),
      ).sort()
    : times;

  return { times, availableTimes };
}

/**
 * Unified computation of time items for a direction.
 * directionIndex 0 = forward (key from other direction = backward)
 * directionIndex 1 = backward (key from other direction = forward)
 */
function computeDirectionTimeItems(
  offers: flightEntity.types.MappedOffer[],
  activeOffer: ticketDetails.types.MappedOffer | undefined,
  directionIndex: 0 | 1,
  otherDirectionMapping:
    | Record<string, directFlightsTypes.TimesMappingEntry>
    | undefined,
  isRoundTrip: boolean,
): TimeItem[] {
  if (offers.length === 0) {
    return [];
  }
  if (directionIndex === 1 && !activeOffer) {
    return [];
  }

  const otherDirectionIndex = directionIndex === 0 ? 1 : 0;
  const { time: currentOtherTime, date: currentOtherDate } =
    directFlightsLib.getCurrentFlightTimeAndDate(
      activeOffer,
      otherDirectionIndex,
    );
  const otherKey =
    currentOtherTime && currentOtherDate
      ? `${currentOtherTime}-${currentOtherDate}`
      : '';

  const timeMap = new Map<string, TimeMapValue>();

  let allTimes: string[] = [];
  let availableTimes: string[] = [];
  let date = '';

  if (isRoundTrip && otherKey && otherDirectionMapping) {
    const data = otherDirectionMapping[otherKey] || null;
    if (data) {
      allTimes = data.times;
      availableTimes = data.availableTimes;
      date = data.date;
    }
  }

  if (allTimes.length === 0) {
    const fallback = getTimesFromOffersFallback(
      offers,
      directionIndex,
      isRoundTrip && currentOtherTime && currentOtherDate
        ? { time: currentOtherTime, date: currentOtherDate }
        : undefined,
    );
    allTimes = fallback.times;
    availableTimes = fallback.availableTimes;
  }

  if (date === '' && offers.length > 0) {
    const dep = offers.find(o => o.flights[directionIndex]?.segments[0]?.dep)
      ?.flights[directionIndex]?.segments[0]?.dep;
    date = dep?.at ? new Date(dep.at).toISOString().slice(0, 10) : '';
  }

  // Pass 1: add only enabled slots (offers matching current other direction)
  offers.forEach(offer => {
    const { time: offerOtherTime, date: offerOtherDate } =
      directFlightsLib.getCurrentFlightTimeAndDate(offer, otherDirectionIndex);
    const { time, date: offerDate } =
      directFlightsLib.getCurrentFlightTimeAndDate(offer, directionIndex);

    if (!time) return;
    if (isRoundTrip && allTimes.length > 0 && !allTimes.includes(time)) return;

    const matchesCurrentOther =
      offerOtherTime === currentOtherTime &&
      offerOtherDate === currentOtherDate;
    const isAvailable =
      !isRoundTrip ||
      availableTimes.length === 0 ||
      availableTimes.includes(time);
    const isDisabled = isRoundTrip && !isAvailable;

    if (isRoundTrip && isAvailable && !matchesCurrentOther) return;

    const key = isRoundTrip ? `${time}-${offerDate || date}` : time;
    updateTimeMapWithLowestPrice(
      timeMap,
      key,
      Number(offer.price.amount),
      offerDate || date,
      isDisabled,
    );
  });

  // Pass 2: fill missing disabled slots from any offer
  if (isRoundTrip && allTimes.length > 0) {
    allTimes.forEach(time => {
      const key = `${time}-${date}`;
      if (timeMap.has(key)) return;

      const offerWithTime = offers.find(o => {
        const dep = o.flights[directionIndex]?.segments[0]?.dep;
        return dep?.time === time;
      });
      if (offerWithTime) {
        const isDisabled = !availableTimes.includes(time);
        timeMap.set(key, {
          price: Number(offerWithTime.price.amount),
          date,
          isDisabled,
        });
      }
    });
  }

  const { time: currentTime, date: currentDate } =
    directFlightsLib.getCurrentFlightTimeAndDate(activeOffer, directionIndex);

  const selectedPrice = Number(activeOffer?.price.amount || 0);

  return buildTimeItemsFromMap(
    timeMap,
    currentTime,
    currentDate,
    selectedPrice,
    isRoundTrip,
  );
}

/**
 * Computes time items for the first direction (forward flight).
 * For RT: shows ALL forward times; unavailable for current backward time are disabled.
 */
export function computeFirstDirectionTimeItems(
  offers: flightEntity.types.MappedOffer[],
  activeOffer: ticketDetails.types.MappedOffer | undefined,
  isRoundTrip: boolean,
  forwardTimesMapping?: directFlightsTypes.ForwardTimesMapping,
): TimeItem[] {
  return computeDirectionTimeItems(
    offers,
    activeOffer,
    0,
    forwardTimesMapping,
    isRoundTrip,
  );
}

/**
 * Computes time items for the second direction (backward flight, RT only).
 * Shows ALL backward times; unavailable ones for current forward time are disabled.
 */
export function computeSecondDirectionTimeItems(
  offers: flightEntity.types.MappedOffer[],
  activeOffer: ticketDetails.types.MappedOffer | undefined,
  backwardTimesMapping: directFlightsTypes.BackwardTimesMapping | undefined,
): TimeItem[] {
  return computeDirectionTimeItems(
    offers,
    activeOffer,
    1,
    backwardTimesMapping,
    true,
  );
}
